/**
 * userPreferencesService
 * Persiste/recupera o objeto JSON `preferences` em `public.user_profiles`.
 *
 * Estrutura armazenada (chaves opcionais):
 *   { theme, viewMode, density, language }
 *
 * Falhas (coluna ausente, RLS, tabela ausente) NÃO interrompem o app — o
 * fallback é o LocalStorage local da preferência. Mantemos um cache de
 * disponibilidade para evitar ruído em logs.
 */
import { supabase } from '../lib/supabaseClient';

export type PreferencesPayload = {
  theme?: 'light' | 'midnight' | 'black';
  viewMode?: 'auto' | 'admin' | 'gestor' | 'usuario';
  density?: 'compact' | 'normal' | 'comfortable';
  language?: 'pt-BR' | 'en-US' | 'es-ES';
};

const SCHEMA_CACHE_KEY = 'workflow_user_preferences_schema_state';
const SCHEMA_TTL_MS = 6 * 60 * 60 * 1000;

let columnAvailable: boolean | null = null;

const isMissingColumnError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const code = 'code' in error ? String((error as { code?: string }).code ?? '') : '';
  const message = 'message' in error ? String((error as { message?: string }).message ?? '') : '';
  // PostgREST: column not found (42703) ou esquema não encontrado (PGRST204)
  return code === '42703' || code === 'PGRST204' || message.toLowerCase().includes('preferences');
};

const readCachedAvailability = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SCHEMA_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { available?: boolean; updatedAt?: number };
    const updatedAt = Number(parsed?.updatedAt ?? 0);
    if (!updatedAt || Date.now() - updatedAt > SCHEMA_TTL_MS) {
      window.localStorage.removeItem(SCHEMA_CACHE_KEY);
      return null;
    }
    return typeof parsed?.available === 'boolean' ? parsed.available : null;
  } catch {
    return null;
  }
};

const writeCachedAvailability = (available: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      SCHEMA_CACHE_KEY,
      JSON.stringify({ available, updatedAt: Date.now() }),
    );
  } catch {
    /* ignore */
  }
};

const getAvailability = (): boolean | null => {
  if (columnAvailable !== null) return columnAvailable;
  const cached = readCachedAvailability();
  if (cached !== null) columnAvailable = cached;
  return columnAvailable;
};

const setAvailability = (value: boolean) => {
  columnAvailable = value;
  writeCachedAvailability(value);
};

export async function getUserPreferences(userId: string): Promise<PreferencesPayload | null> {
  if (!userId) return null;
  if (getAvailability() === false) return null;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('preferences')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    if (isMissingColumnError(error)) {
      setAvailability(false);
      return null;
    }
    console.warn('[userPreferencesService] read error:', error);
    return null;
  }

  setAvailability(true);
  const raw = (data?.preferences ?? null) as PreferencesPayload | null;
  return raw && typeof raw === 'object' ? raw : null;
}

export async function upsertUserPreferences(userId: string, patch: PreferencesPayload): Promise<void> {
  if (!userId) return;
  if (getAvailability() === false) return;

  // Lê estado atual para fazer merge defensivo (preferences é JSON)
  const current = (await getUserPreferences(userId)) ?? {};
  const next: PreferencesPayload = { ...current, ...patch };

  const { error } = await supabase
    .from('user_profiles')
    .update({ preferences: next, updated_at: new Date().toISOString() })
    .eq('user_id', userId);

  if (error) {
    if (isMissingColumnError(error)) {
      setAvailability(false);
      return;
    }
    console.warn('[userPreferencesService] write error:', error);
    return;
  }

  setAvailability(true);
}
